import { Editor } from "@monaco-editor/react";
import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { Link } from "react-router";
import { API_URL } from "../../api/auth";

export const GH_ACTION_YAML = `- name: Run desplega.ai tests
  uses: desplega-ai/desplega.ai-action@v0.4.1 # or @main
  with:
    apiKey: \${{ secrets.DESPLEGA_API_KEY }}
    suiteIds: '<suite_id>'
    # Set suite variable overrides if needed
    vars: |
      base_url=\${{ steps.vercel.outputs.url }}
      login_password=\${{ secrets.TEST_PASSWORD }}`;

export const GL_ACTION_YAML = `trigger_desplega:
  stage: deploy
  variables:
    # Set suite variable overrides if needed
    REQUEST_BODY: '{"suite_ids": ["<suite_id>"],"vars":[{"key":"base_url","value":"$CI_ENVIRONMENT_URL"},{"key":"login_password","value":"$TEST_PASSWORD"}]}'
    API_TOKEN: $DESPLEGA_API_TOKEN # Use GitLab secret variable
  script: >
    curl --request POST --url "${API_URL}/external/actions/trigger"
    --header "Authorization: Bearer $API_TOKEN"
    --data "\${REQUEST_BODY}"`;

interface CITabProps {
  maybeApiKey?: string;
}

export const CITab: React.FC<CITabProps> = ({ maybeApiKey }) => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h5" component="div">
            Github Action
          </Typography>
          <Typography variant="body2" mt={1}>
            You can use the desplega.ai Github Action to run your tests automatically on every push
            to <code>main</code> or pull request.
          </Typography>
          <Typography variant="body2" mt={1}>
            Once you generate an API key, you can use it in your Github Action workflow. You just
            need to set it up as a secret in your repository settings, and then use it in your
            workflow file.
          </Typography>
          <Box mt={2} border={1} borderColor="divider" p={1} borderRadius={1}>
            <Editor
              height="200px"
              defaultLanguage="yaml"
              value={GH_ACTION_YAML}
              options={{
                readOnly: true,
                minimap: {
                  enabled: false,
                },
                scrollBeyondLastLine: false,
              }}
            />
            <Stack direction="row" spacing={2} mt={1} alignItems="center" justifyContent="flex-end">
              {maybeApiKey && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(maybeApiKey);

                    enqueueSnackbar("API key copied to clipboard", {
                      variant: "success",
                      anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                      },
                    });
                  }}
                  sx={{ marginTop: 1 }}
                >
                  Copy API key
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(GH_ACTION_YAML);

                  enqueueSnackbar("YAML copied to clipboard", {
                    variant: "success",
                    anchorOrigin: {
                      horizontal: "right",
                      vertical: "bottom",
                    },
                  });
                }}
                sx={{ marginTop: 1 }}
              >
                Copy code
              </Button>
            </Stack>
          </Box>
          <Typography variant="body2" mt={1} color="text.secondary">
            If you want to learn more, check the{" "}
            <Link to="https://github.com/desplega-ai/desplega.ai-action" target="_blank">
              action repository
            </Link>
            .
          </Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h5" component="div">
            Gitlab Action
          </Typography>
          <Typography variant="body2" mt={1}>
            You can use the desplega.ai in your Gitlab CI/CD pipeline to run automatically on every
            push to <code>main</code> or pull request.
          </Typography>
          <Typography variant="body2" mt={1}>
            Once you generate an API key, you can use it in your Gitlbal CI/CD workflow. You just
            need to set it up as a secret in your repository settings, and then use it in your
            workflow file.
          </Typography>
          <Box mt={2} border={1} borderColor="divider" p={1} borderRadius={1}>
            <Editor
              height="200px"
              defaultLanguage="yaml"
              value={GL_ACTION_YAML}
              options={{
                readOnly: true,
                minimap: {
                  enabled: false,
                },
                scrollBeyondLastLine: false,
              }}
            />
            <Stack direction="row" spacing={2} mt={1} alignItems="center" justifyContent="flex-end">
              {maybeApiKey && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(maybeApiKey);

                    enqueueSnackbar("API key copied to clipboard", {
                      variant: "success",
                      anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                      },
                    });
                  }}
                  sx={{ marginTop: 1 }}
                >
                  Copy API key
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(GL_ACTION_YAML);

                  enqueueSnackbar("Gitlab YAML copied to clipboard", {
                    variant: "success",
                    anchorOrigin: {
                      horizontal: "right",
                      vertical: "bottom",
                    },
                  });
                }}
                sx={{ marginTop: 1 }}
              >
                Copy code
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};
